import React, { useEffect, useState } from 'react';

import AttendeesDialog from '../../../../components/AttendeesDialog';
import { getCurrentUserId } from '../../../../services/auth';
import { useGetStudentsQuery } from '../../../../services/students';
import {
  useBatchUnbookTutorialMutation, useBookTutorialMutation, useGetTutorialQuery,
} from '../../../../services/tutorials';
import { Tutorial } from '../../../../services/tutorials/types';
import useError from '../../../../utils/react/hooks/useError';
import useSuccess from '../../../../utils/react/hooks/useSuccess';

import { studentToAttendee } from './utils';

interface TutorialAttendeesDialogProps {
  open: boolean,
  selectedTutorial: Tutorial,
  handleClose: () => void,
  isAdmin: boolean,
}

const TutorialAttendeesDialog = ({
  open,
  selectedTutorial,
  handleClose = () => {},
  isAdmin,
}: TutorialAttendeesDialogProps) => {
  const [attendeeSearch, setAttendeeSearch] = useState('');

  const {
    data: students,
    isLoading,
    refetch,
  } = useGetStudentsQuery({ search: attendeeSearch, excludeDisabled: true });

  const userId = getCurrentUserId();

  const { data: tutorial,
    isFetching:
    isTutorialLoading } = useGetTutorialQuery(
    { course: selectedTutorial.course, id: selectedTutorial.startDatetimeIdentifier });

  const [bookTutorial,
    { isError: isBookingError,
      isSuccess: isBookingSuccess,
      error: bookingError,
      isLoading: isBookingLoading }] = useBookTutorialMutation();

  const [batchUnbookTutorial,
    { isError: isUnbookingError,
      isSuccess: isUnbookingSuccess,
      error: UnbookingError,
      isLoading: isUnbookingLoading }] = useBatchUnbookTutorialMutation();

  useEffect(() => {
    refetch();
  }, [attendeeSearch]);

  const handleAddAttendee = (studentId: string, note?: string) => {
    if (tutorial) {
      bookTutorial({
        course: tutorial?.data.course,
        id: tutorial?.data.startDatetimeIdentifier,
        studentId,
        note,
      });
    }
  };

  const handleRemoveAttendees = (studentIds: any[]) => {
    if (tutorial) {
      batchUnbookTutorial({
        course: tutorial?.data.course,
        id: tutorial?.data.startDatetimeIdentifier,
        studentIds,
      });
    }
  };

  useError(isBookingError, bookingError);
  useSuccess(isBookingSuccess, 'Student Added');
  useError(isUnbookingError, UnbookingError);
  useSuccess(isUnbookingSuccess, 'Student(s) Removed');

  return (
    <AttendeesDialog
      setAttendeeSearch={setAttendeeSearch}
      open={open}
      queryResults={students?.data ?? []}
      isQueryLoading={isLoading}
      attendees={tutorial?.data.attendees?.map((student) => studentToAttendee(student)) ?? []}
      isAttendeesLoading={isTutorialLoading}
      handleClose={handleClose}
      addAttendee={handleAddAttendee}
      removeAttendees={handleRemoveAttendees}
      isLoading={isBookingLoading || isUnbookingLoading}
      editable={isAdmin || tutorial?.data.host === userId}
    />
  );
};

export default TutorialAttendeesDialog;
